import { Box, Button, Center, CircularProgress, Grid, GridItem, ModalOverlay, Spinner, Stack, Text } from '@chakra-ui/react'
import type MarketingListRepo from 'features/ListBuilder/domain/repository/MarketingListRepo'
import { useDownloadListState, useDownloadStats, useListStoreActions, useSelectedList } from 'features/ListBuilder/infra/react/ListBuilderStore'
import { NumberLib } from 'libs/Number'
import happyDogPng from 'presentation/assets/img/3d/happy-dog.png'
import { Modal, ModalContent } from 'presentation/components/Modal'
import { ModalCloseButton } from 'presentation/components/Modal/ModalCloseButton'
import { Pair, PairKey, PairValue } from 'presentation/components/Pair/Pair'
import { toast } from 'presentation/components/Toast'
import { Card, CardBody, CardButton, CardFooter, CardImage, CardPrimaryText, CardPrimaryTitle } from 'presentation/components/molecules/Card'
import { TOAST_PRESET } from 'presentation/const/toast.const'
import { useToastFailedStateTagged } from 'presentation/libs/hooks/useToastFailedState'
import { useListDataDownloadModal, useListDataDownloadModalActions } from 'presentation/screens/ListScreen/components/ListFooter/components/useListDataDownloadModal'
import { useEffect } from 'react'

const getDownloadEnd = (download: MarketingListRepo.DownloadEntry) =>
  download.cursor.offset + download.cursor.limit

const formatLimitValue = (value: number) =>
  Number.isFinite(value) ? NumberLib.formatComma(value) : 'Unlimited'

const formatDownloadRange = (download: MarketingListRepo.DownloadEntry) => {
  if (download.cursor.limit <= 0) return 'Download records 0'

  const start = download.cursor.offset + 1
  const end = getDownloadEnd(download)

  return `Download records ${NumberLib.formatComma(start)} to ${NumberLib.formatComma(end)}`
}

const formatDownloadedAt = (date: Date) =>
  new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date)

const getLimitProgressValue = (limitInfo: MarketingListRepo.LimitInfo) => {
  if (limitInfo.limit === Infinity) return 100
  if (limitInfo.limit <= 0) return 0

  return Math.max(0, Math.min(100, limitInfo.remaining / limitInfo.limit * 100))
}

export const ListDataDownloadModal = () => {
  const modalState = useListDataDownloadModal()
  const modalActions = useListDataDownloadModalActions()
  const downloadStatsState = useDownloadStats()
  const listActions = useListStoreActions()
  const downloadListState = useDownloadListState()
  const selectedListState = useSelectedList()

  const modalListId = modalState.status === 'open' ? modalState.payload.listId : null
  const selectedList = selectedListState._tag === 'Success' && selectedListState.list.id === modalListId
    ? selectedListState.list
    : null
  const currentMemberCount = selectedList?.memberCount ?? null

  useEffect(() => {
    if (modalState.status !== 'open') return

    listActions.getDownloadStats(modalState.payload.listId)
  }, [modalState.status])

  useToastFailedStateTagged(downloadStatsState, {
    title: 'Failed to fetch download stats',
    message: TOAST_PRESET.GENERIC_ERROR.message,
  })

  useToastFailedStateTagged(downloadListState, {
    title: 'Failed to download list',
    message: TOAST_PRESET.GENERIC_ERROR.message,
  })

  const handleDownload = (mode: MarketingListRepo.DownloadMode) => {
    if (modalState.status !== 'open') return

    listActions.downloadList(modalState.payload.listId, mode)
    modalActions.close()
    toast.info({
      title: 'Download Starting...',
      message: 'Your list will download shortly',
    })
  }

  useEffect(() => {
    if (downloadStatsState._tag !== 'Success') return

    const limitInfo = downloadStatsState.downloadStats.limitInfoByInterval.monthly
    const hasPreviousDownloads = downloadStatsState.downloadStats.downloads.length > 0

    if (limitInfo.limit !== Infinity) return
    if (hasPreviousDownloads) return

    handleDownload({ _tag: 'CreateDownload', cursorOffset: 0 })
  }, [downloadStatsState])

  return (
    <Modal
      isOpen={modalState.status === 'open'}
      onClose={modalActions.close}
    >
      <ModalOverlay />
      <ModalContent>
        {modalState.status === 'open' && (
          <Card
            variant='modal.alert'
            size='modal.alert.xn'
            colorScheme='modal.alert.neutral'
          >
            <CardImage src={happyDogPng} />
            <ModalCloseButton onClose={modalActions.close} />

            <CardPrimaryTitle>
              Download List Data
            </CardPrimaryTitle>

            {downloadStatsState._tag === 'Success'
              ? (() => {
                const stats = downloadStatsState.downloadStats
                const limitInfo = stats.limitInfoByInterval.monthly
                const latestDownload = stats.downloads[stats.downloads.length - 1] ?? null
                const nextCursorOffset = latestDownload ? getDownloadEnd(latestDownload) : 0
                const hasPreviousDownloads = stats.downloads.length > 0
                const hasListRows = currentMemberCount === null || currentMemberCount > 0
                const canCreateDownload = hasListRows && (limitInfo.limit === Infinity || limitInfo.remaining > 0)
                const canContinue = latestDownload !== null && (
                  currentMemberCount === null || nextCursorOffset < currentMemberCount
                )
                const remainingListRows = currentMemberCount === null
                  ? null
                  : Math.max(currentMemberCount - nextCursorOffset, 0)

                return (
                  <>
                    <CardBody>
                      <CardPrimaryText>
                        {hasPreviousDownloads
                          ? 'Choose a list download.'
                          : 'Continue with the download?'}
                      </CardPrimaryText>

                      <Box
                        mt={5}
                        p={3}
                        borderRadius={4}
                        bg='highlight.100'
                        boxShadow='primary.m'
                      >
                        <Grid
                          templateColumns='min-content 1fr'
                          columnGap={2}
                          rowGap={3}
                        >
                          <GridItem>
                            <Box pos='relative'>
                              <CircularProgress
                                capIsRound
                                transform='scaleX(-1)'
                                value={getLimitProgressValue(limitInfo)}
                                color='positivesat.500'
                                trackColor='grayweak.500'
                                size='160px'
                                thickness={4}
                              />

                              <Center
                                flexDir='column'
                                pos='absolute'
                                top={0}
                                left={0}
                                right={0}
                                bottom={0}
                                display='flex'
                              >
                                <Text color='positivesat.500' fontSize='26px' fontWeight='700'>
                                  {formatLimitValue(limitInfo.remaining)}
                                </Text>
                                <Text textAlign='center' textStyle='bodyMFat' color='graystrong.600'>
                                  Lead List
                                  <br />
                                  Exports Left
                                </Text>
                              </Center>
                            </Box>
                          </GridItem>
                          <GridItem
                            display='flex'
                            flexDir='column'
                            justifyContent='center'
                            alignItems='stretch'
                          >
                            <Pair>
                              <PairKey>Monthly Export Limit:</PairKey>
                              <PairValue>{formatLimitValue(limitInfo.limit)}</PairValue>
                            </Pair>

                            <Pair>
                              <PairKey>Exported This Month:</PairKey>
                              <PairValue>{formatLimitValue(limitInfo.consumed)}</PairValue>
                            </Pair>
                          </GridItem>
                        </Grid>
                      </Box>

                      {hasPreviousDownloads && (
                        <Box mt={5}>
                          <Text textStyle='bodyMFat' color='graystrong.600'>
                            Previous List Downloads
                          </Text>

                          <Stack mt={3} spacing={2} maxH='220px' overflowY='auto'>
                            {stats.downloads.map(download => (
                              <Button
                                key={download.id}
                                variant='outline'
                                colorScheme='neutral'
                                size='sm'
                                justifyContent='flex-start'
                                minH='44px'
                                h='auto'
                                whiteSpace='normal'
                                textAlign='left'
                                onClick={() => handleDownload({
                                  _tag: 'ExistingDownload',
                                  downloadId: download.id,
                                })}
                              >
                                <Box as='span'>
                                  <Text as='span' display='block' textStyle='bodyMFat'>
                                    {formatDownloadRange(download)}
                                  </Text>
                                  <Text as='span' display='block' textStyle='bodyS' color='graystrong.500'>
                                    {formatDownloadedAt(download.createdAt)}
                                  </Text>
                                </Box>
                              </Button>
                            ))}
                          </Stack>

                          {remainingListRows !== null && (
                            <Text mt={4} textStyle='bodyM' color='graystrong.600'>
                              {canContinue
                                ? `${NumberLib.formatComma(remainingListRows)} records remain after the latest list download, starting at record ${NumberLib.formatComma(nextCursorOffset + 1)}.`
                                : 'The latest list download already reaches the current list size.'}
                            </Text>
                          )}
                        </Box>
                      )}
                    </CardBody>

                    <CardFooter flexWrap='wrap' rowGap={2}>
                      <CardButton
                        variant='outline'
                        colorScheme='neutral'
                        size='md'
                        onClick={modalActions.close}
                      >
                        Cancel
                      </CardButton>
                      <CardButton
                        variant={hasPreviousDownloads ? 'outline' : 'solid'}
                        colorScheme='neutral'
                        size='md'
                        isDisabled={!canCreateDownload}
                        onClick={() => handleDownload({
                          _tag: 'CreateDownload',
                          cursorOffset: 0,
                        })}
                      >
                        {hasPreviousDownloads ? 'Start New Download' : 'Download'}
                      </CardButton>
                      {hasPreviousDownloads && (
                        <CardButton
                          variant='solid'
                          colorScheme='neutral'
                          size='md'
                          isDisabled={!canCreateDownload || !canContinue}
                          onClick={() => handleDownload({
                            _tag: 'CreateDownload',
                            cursorOffset: nextCursorOffset,
                          })}
                        >
                          Continue Download
                        </CardButton>
                      )}
                    </CardFooter>
                  </>
                )
              })()
              : (
                <Center py={5}>
                  <Spinner />
                </Center>
              )}
          </Card>
        )}
      </ModalContent>
    </Modal>
  )
}
